import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { MktCard, MktButton, Pill, Tag, Skeleton } from './marketplaceUi';
import {
  getArchetypes,
  getArchetypeProfile,
  type SubCategory,
  type Archetype,
  type ArchetypeProfile,
} from './marketplaceApi';

const FIELD_LABELS: Record<string, string> = {
  age: 'Age', gender: 'Gender', income: 'Household income', income_level: 'Income',
  children: 'Children', location: 'Location', ethnicity: 'Ethnicity',
  education: 'Education', employment: 'Employment', occupation: 'Occupation',
};

const initials = (name: string) =>
  name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

// Representative questions per sub-category (from the landing mockup) — shown
// unblurred on the public landing to build curiosity; the response is walled.
const SAMPLE_Q: Record<string, string> = {
  cereal: 'Why do you reach for cereal on a busy morning?',
  granola: 'What makes a granola worth paying more for?',
  oats: 'Describe your oat routine in the morning.',
  milk: 'Have you ever seriously considered switching to plant milk?',
  bars: 'When do you reach for a breakfast bar over something else?',
  yogurt: 'Why Greek over regular yoghurt?',
};
const LOCKED_RESPONSE =
  "Honestly it's the only thing I can make with one hand while getting everyone out the door. " +
  "I have my two or three go-tos that I rotate through, and I'll grab whatever's on offer — but I'm " +
  'pretty particular about the texture and how much sugar is in it.';

// Curiosity subline for the public landing — category + a couple of real
// demographic fields, without revealing the full profile.
const teaserSubline = (profile: ArchetypeProfile): string => {
  const sp = profile.sample_profile || {};
  const teaser = [sp.age, sp.income || sp.income_level, sp.location]
    .map((v) => (v == null ? '' : String(v).trim()))
    .filter(Boolean);
  return [profile.sub_category_name, ...teaser].filter(Boolean).join(' · ');
};

/**
 * Sub-category tabs → archetype list → live Twin profile preview. Shared by the
 * dashboard and the public landing page. `ctaLabel`/`onCta` lets the landing
 * route the preview CTA to the signup gate; by default it deep-links into the
 * run flow with the segment preset.
 */
const TwinExplorer: React.FC<{
  subs: SubCategory[];
  ctaLabel?: string;
  onCta?: (archetypeId: string, subCategory: string) => void;
  /** Public landing: blur the profile behind a sign-up wall. */
  locked?: boolean;
}> = ({ subs, ctaLabel = 'Run a study with this segment →', onCta, locked = false }) => {
  const navigate = useNavigate();
  const [selectedSub, setSelectedSub] = useState<string | null>(null);
  const [archetypes, setArchetypes] = useState<Archetype[]>([]);
  const [archLoading, setArchLoading] = useState(false);
  const [selectedArchId, setSelectedArchId] = useState<string | null>(null);
  const [profile, setProfile] = useState<ArchetypeProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    if (subs.length && !selectedSub) setSelectedSub(subs[0].slug);
  }, [subs, selectedSub]);

  useEffect(() => {
    if (!selectedSub) return;
    setArchetypes([]); setSelectedArchId(null); setArchLoading(true);
    getArchetypes(selectedSub)
      .then((a) => { setArchetypes(a); setSelectedArchId(a[0]?.archetype_id ?? null); })
      .catch(() => { /* keep empty */ })
      .finally(() => setArchLoading(false));
  }, [selectedSub]);

  useEffect(() => {
    if (!selectedArchId) { setProfile(null); return; }
    setProfileLoading(true);
    getArchetypeProfile(selectedArchId)
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setProfileLoading(false));
  }, [selectedArchId]);

  const runCta = () => {
    if (!profile) return;
    if (onCta) onCta(profile.archetype_id, profile.sub_category);
    else navigate('/marketplace/run', { state: { archetypeId: profile.archetype_id, subCategory: profile.sub_category } });
  };

  return (
    <div>
      {/* Sub-category tabs */}
      <div className="mb-5 flex flex-wrap gap-2">
        {subs.map((sc) => (
          <Pill key={sc.slug} active={selectedSub === sc.slug} onClick={() => setSelectedSub(sc.slug)}>
            {sc.name}
          </Pill>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
        {/* Left — archetype list */}
        <div>
          <div className="mb-2.5 text-[11px] font-medium uppercase tracking-[0.05em] text-[#6b6b6b]">Consumer profiles</div>
          <div className="flex flex-col gap-2">
            {archLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="rounded-xl border border-black/10 bg-white px-3.5 py-3">
                    <Skeleton className="h-3.5 w-2/3" />
                    <Skeleton className="mt-1.5 h-2.5 w-1/3" />
                  </div>
                ))
              : archetypes.map((a) => {
                  const sel = a.archetype_id === selectedArchId;
                  return (
                    <button
                      key={a.archetype_id}
                      onClick={() => setSelectedArchId(a.archetype_id)}
                      className={[
                        'rounded-xl border px-3.5 py-3 text-left transition-colors',
                        sel ? 'border-primary bg-[#E8FCFA]' : 'border-black/10 bg-white hover:border-black/20',
                      ].join(' ')}
                    >
                      <div className="text-[13px] font-medium text-[#111]">{a.name}</div>
                      <div className="text-[11px] text-[#999]">{a.twin_count} Twins</div>
                    </button>
                  );
                })}
          </div>
        </div>

        {/* Right — Twin preview */}
        <div>
          <div className="mb-2.5 text-[11px] font-medium uppercase tracking-[0.05em] text-[#6b6b6b]">Twin preview</div>
          <MktCard className="overflow-hidden">
            {archLoading || profileLoading || !profile ? (
              <div className="p-5">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-11 w-11 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3.5 w-1/2" />
                    <Skeleton className="h-2.5 w-2/3" />
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="space-y-1.5">
                      <Skeleton className="h-2.5 w-1/2" />
                      <Skeleton className="h-3 w-3/4" />
                    </div>
                  ))}
                </div>
                <Skeleton className="mt-5 h-9 w-full rounded-lg" />
              </div>
            ) : (
              <>
                <div className="border-b border-black/10 p-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#E8FCFA] text-[14px] font-medium text-[#0B7C74]">
                      {initials(profile.name)}
                    </div>
                    <div>
                      <div className="text-[15px] font-medium text-[#111]">{profile.name}</div>
                      <div className="text-[12px] text-[#6b6b6b]">
                        {locked ? teaserSubline(profile) : `${profile.sub_category_name} · ${profile.twin_count} Twins`}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Tag>AI Twin</Tag>
                    <span className="inline-flex items-center rounded-full bg-[#f5f5f3] px-2 py-0.5 text-[11px] font-medium text-[#6b6b6b]">
                      {profile.sub_category_name}
                    </span>
                  </div>
                </div>

                {locked ? (
                  <>
                    {/* Public landing: show a real sample question (curiosity) but
                        wall the response behind sign-up. */}
                    <div className="border-b border-black/10 p-5">
                      <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.04em] text-[#6b6b6b]">Sample question</div>
                      <p className="text-[13px] italic leading-relaxed text-[#374151]">
                        “{SAMPLE_Q[selectedSub || ''] || 'What drives your choices in this category?'}”
                      </p>
                    </div>
                    <div className="relative border-b border-black/10 p-5">
                      <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.04em] text-[#6b6b6b]">{profile.name.split(' ')[0]}’s response</div>
                      <p className="select-none text-[13px] leading-relaxed text-[#374151] blur-[5px] pointer-events-none">
                        {LOCKED_RESPONSE}
                      </p>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3.5 py-2 text-[12px] text-[#6b6b6b] shadow-sm">
                          <Lock className="h-3.5 w-3.5" /> Sign up to read this response
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="border-b border-black/10 p-5">
                    <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.04em] text-[#6b6b6b]">Representative Twin</div>
                    {Object.keys(profile.sample_profile || {}).length === 0 ? (
                      <p className="text-[13px] text-[#999]">Full profile available after you run a study.</p>
                    ) : (
                      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {Object.entries(profile.sample_profile).map(([k, v]) => (
                          <div key={k}>
                            <dt className="text-[11px] text-[#999]">{FIELD_LABELS[k] || k}</dt>
                            <dd className="text-[13px] text-[#374151]">{String(v)}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                )}

                <div className="p-5">
                  <MktButton fullWidth onClick={runCta}>{ctaLabel}</MktButton>
                  {locked && (
                    <p className="mt-2 text-center text-[11px] text-[#999]">First use case free · No credit card</p>
                  )}
                </div>
              </>
            )}
          </MktCard>
        </div>
      </div>

      {locked && (
        <div className="mt-4 flex items-center justify-center gap-1.5 text-[12px] text-[#6b6b6b]">
          <Lock className="h-3.5 w-3.5" />
          {(() => {
            const c = subs.find((s) => s.slug === selectedSub)?.twin_count;
            return c ? `${c.toLocaleString()} Twins in this category — sign up to query any of them` : 'Sign up to query any Twin';
          })()}
        </div>
      )}
    </div>
  );
};

export default TwinExplorer;
